/**
 * Utility functions to handle launching the app from a social app and exiting back to it.
 *
 * When the app is launched from a social app, it should include the following query parameters:
 * - openedFrom=social-app
 * - exitUrl=<URL to return to when exiting the app>
 *
 * This module will consume these parameters, store the context in session storage, and provide functions
 * to check if the app was launched from a social app and to exit back to it.
 */

const SOCIAL_APP_SOURCE_PARAM = 'openedFrom';
const SOCIAL_APP_EXIT_URL_PARAM = 'exitUrl';
const SOCIAL_APP_SOURCE_VALUE = 'social-app';
const SOCIAL_APP_SESSION_STORAGE_KEY = 'opened-from-social-app';
const SOCIAL_APP_EXIT_URL_SESSION_STORAGE_KEY = 'social-app-exit-url';

export function consumeSocialAppLaunchParams(): boolean {
  const url = new URL(window.location.href);
  const openedFrom = url.searchParams.get(SOCIAL_APP_SOURCE_PARAM);
  const exitUrl = url.searchParams.get(SOCIAL_APP_EXIT_URL_PARAM);

  if (openedFrom === SOCIAL_APP_SOURCE_VALUE) {
    sessionStorage.setItem(SOCIAL_APP_SESSION_STORAGE_KEY, 'true');

    if (exitUrl) {
      sessionStorage.setItem(SOCIAL_APP_EXIT_URL_SESSION_STORAGE_KEY, exitUrl);
    }

    url.searchParams.delete(SOCIAL_APP_SOURCE_PARAM);
    url.searchParams.delete(SOCIAL_APP_EXIT_URL_PARAM);
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
  }

  return isRunningFromSocialApp();
}

export function isRunningFromSocialApp(): boolean {
  return sessionStorage.getItem(SOCIAL_APP_SESSION_STORAGE_KEY) === 'true';
}

export function clearSocialAppLaunchContext(): void {
  sessionStorage.removeItem(SOCIAL_APP_SESSION_STORAGE_KEY);
  sessionStorage.removeItem(SOCIAL_APP_EXIT_URL_SESSION_STORAGE_KEY);
}

export function exitToSocialApp(): void {
  const exitUrl = sessionStorage.getItem(SOCIAL_APP_EXIT_URL_SESSION_STORAGE_KEY);

  if (!exitUrl) {
    console.warn('Missing Social App exitUrl.');
    return;
  }

  clearSocialAppLaunchContext();
  window.location.replace(exitUrl);
}
